// utils/recordQualifier.js
//
// THE QUALIFIED BADGE (docs/platform-standards.md §1, "Qualify outside your
// collection"): a card or an item shown anywhere OUTSIDE the collection that
// owns it carries that collection in its badge — a small stacked collection
// tile and the collection's name, a thin divider, then the record exactly as
// it always reads. Inside its own collection it reads as it always has.
//
// This module is the rule, and nothing else: which collection owns a record,
// whether a surface in collection X qualifies it, and how the segment is
// named. The identity of the owning collection (name, icon, colour) comes from
// hooks/useCollectionIdentity; the surface's own collection from
// contexts/CollectionScopeContext. RecordBadge puts the three together, so a
// caller only ever says "this record lives in <collection>".

import { borrowedFromOf, linkedFromOf } from './linkedRecords';
import { getCardScope } from './cardScopes';
import { normalizeId } from './ids';

const idOf = raw => {
	if (!raw) return null;
	if (typeof raw === 'string') return raw.trim() || null;
	if (typeof raw !== 'object') return null;
	return normalizeId(raw._id || raw.collection_id || raw.collectionId || raw.id) || null;
};

const textOf = value => (typeof value === 'string' ? value.trim() : '');

// A sub-collection's parent, by name: a list row's `parent`, a document's
// `hierarchy.parent`, or an identity's own `parentName`.
const parentNameOf = raw => {
	const parent = raw.hierarchy?.parent || raw.parent;
	return textOf(parent && typeof parent === 'object' ? parent.name : '') || textOf(raw.parentName) || null;
};

/**
 * `{ _id, name, icon, color, parentName? }` from any shape a collection's
 * identity arrives in — a collection document, a list row, a list row's
 * `parent`, a hierarchy summary, a row's `linkedFrom`, a card's
 * `borrowedFrom.collection`, a pin's `collection`, a draft's — or null when it
 * carries no id AND name (a bare id is not an identity;
 * hooks/useCollectionIdentity resolves those). `parentName` is there only for
 * a sub-collection whose payload names its parent: two sub-collections called
 * "Health" under different parents are told apart by it.
 */
export function collectionIdentityOf(raw) {
	if (!raw || typeof raw !== 'object') return null;
	const id = idOf(raw);
	const name = textOf(raw.name);
	if (!id || !name) return null;
	const parentName = parentNameOf(raw);
	return { _id: id, name, icon: raw.icon || null, color: raw.color || null, ...(parentName ? { parentName } : null) };
}

/** The collection id a qualifier argument names: an id string, or anything collectionIdentityOf reads. */
export const qualifierIdOf = raw => idOf(raw);

/**
 * The collection that OWNS a record, as an id (or null when nothing owns it —
 * a global card, an unsaved item):
 *
 *   item  a linked row's source (`linkedFrom`), else its `collection_id`
 *   card  a borrowed card's lender (`borrowedFrom`), else the owning
 *         collection of a `scope: 'collection'` card; global, transitional
 *         and legacy cards belong to no collection
 */
export function owningCollectionIdOf(record, kind = 'item') {
	if (!record || typeof record !== 'object') return null;
	if (kind === 'card') {
		const lender = borrowedFromOf(record);
		if (lender) return lender.collectionId;
		return getCardScope(record) === 'collection' ? (normalizeId(record.collection_id) || null) : null;
	}
	if (kind === 'item') {
		return linkedFromOf(record)?.collectionId || normalizeId(record.collection_id) || null;
	}
	return null;
}

/**
 * THE RULE. The collection a record shown in `contextCollectionId` must be
 * qualified with — its owner's id — or null when it needs none: its owner IS
 * the surface's collection, or it has no owner. A surface with no collection
 * (null: Home, the palette, the inbox, History…) qualifies every record whose
 * owner is known. A sub-collection's record in its parent, a parent's record
 * in a child, a linked record and a global collection's record all qualify:
 * the comparison is the owner's id against the surface's, nothing looser.
 *
 * `record` is the record (with `kind`), or directly the owner — an id or an
 * identity object — when the caller already knows it.
 */
export function qualifierFor(record, contextCollectionId = null, { kind = null } = {}) {
	const owner = kind ? owningCollectionIdOf(record, kind) : qualifierIdOf(record);
	if (!owner) return null;
	const context = normalizeId(contextCollectionId) || null;
	return owner === context ? null : owner;
}

/**
 * A collection's whole name as the qualifier's tooltip and spoken text say it:
 * "Health, in Biewend" for a sub-collection whose parent is known, else just
 * "Biewend". The visible segment prints the name alone and lets CSS cut it.
 */
export function qualifierFullName(identity) {
	const name = textOf(identity?.name);
	const parent = textOf(identity?.parentName);
	return parent ? `${name}, in ${parent}` : name;
}

/**
 * HOW a record shown here is related to the collection it lives in, when that
 * is more than "it lives there" (utils/linkedRecords rowSourceOf `via`):
 *
 *   link    a linked row — another collection shares it through a collection
 *           link; read-only here (the segment carries the lock)
 *   family  a sub-collection's record in its parent's list; an ordinary
 *           record of the family, edited where it lives
 *   parent  a parent's record read by reference in a sub-collection;
 *           read-only here
 */
const RELATION_WORDS = { link: 'linked, read-only', parent: 'parent collection, read-only', family: 'sub-collection' };

/** What a screen reader hears after the record: "in Clinic (linked, read-only)". */
export function qualifierSpokenText(identity, relation = null) {
	const full = qualifierFullName(identity);
	if (!full) return '';
	const words = RELATION_WORDS[relation];
	return `in ${full}${words ? ` (${words})` : ''}`;
}

/**
 * How a qualified badge is spoken — the record first, then what it is, then
 * where it lives and how: "Dune, Book, in Biewend" for an item, "Book, in
 * Biewend" for a card, "Visit 3, Visit, in Clinic (linked, read-only)" for a
 * linked row.
 */
export function qualifiedAccessibleName({ title, kicker, identity, relation = null }) {
	const parts = [title, kicker].map(textOf).filter(Boolean);
	const where = identity ? qualifierSpokenText(identity, relation) : '';
	return [...parts, where].filter(Boolean).join(', ');
}

/** The segment's tooltip: where the record lives, and the relation for a row a list shows from somewhere else. */
export function qualifierNote(identity, relation = null) {
	const full = qualifierFullName(identity);
	if (relation === 'link') return `Shared from ${full} through a collection link — read-only here`;
	if (relation === 'family') return `Lives in ${textOf(identity?.name)}, a sub-collection of this one`;
	if (relation === 'parent') return `Lives in ${full}, the parent collection — read-only here`;
	return `In ${full}`;
}

/**
 * `rowSourceOf`'s answer as what RecordBadge's `collection` takes: the source's
 * identity when the API named it, else its id alone — so the resolver names it
 * or nothing does, and a placeholder ("another collection") is never shown or
 * remembered as a collection's name. Pair it with `collectionRelation={source.via}`.
 */
export function rowSourceIdentity(source) {
	if (!source) return null;
	if (source.named === false) return source.collectionId || null;
	return collectionIdentityOf({ _id: source.collectionId, name: source.name, icon: source.icon, color: source.color, parentName: source.parentName })
		|| source.collectionId || null;
}

/**
 * A row on a surface that belongs to NO collection (the tag page, T5) as what
 * RecordBadge's `collection` takes: the collection the row lives in — the
 * identity when the row carries its name (the tag page's `collection: { _id,
 * name? }`), else its id alone, which the resolver names or leaves out. A
 * linked row's source wins, as `owningCollectionIdOf` has it.
 */
export function rowOwnCollection(item) {
	if (!item || typeof item !== 'object') return null;
	const linked = linkedFromOf(item);
	if (linked) return linked.collectionId || null;
	return collectionIdentityOf(item.collection) || normalizeId(item.collection_id) || null;
}
